import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import * as bootstrap from "bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";

function Gallery({ slug }) {
  const [gallery, setGallery] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const fetchGallery = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/user/gallery/${slug}?limit=6&page=${pageNum}`,
        {
          headers: { Authorization: process.env.REACT_APP_API_KEY },
        }
      );

      if (res.data && res.data.success) {
        const newItems = (res.data.data && res.data.data.data) || [];
        setGallery((prev) => [...prev, ...newItems]);

        const nextPageUrl = res.data.data && res.data.data.next_page_url;
        setHasMore(!!nextPageUrl);
      }
    } catch (err) {
      console.error("Error fetching gallery:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const openModal = (index) => {
    setSelectedIndex(index);
    const modalEl = document.getElementById("galleryModal");
    const modal = new bootstrap.Modal(modalEl);
    modal.show();
  };

  const closeModal = () => {
    const modalEl = document.getElementById("galleryModal");
    const modal = bootstrap.Modal.getInstance(modalEl);
    if (modal) modal.hide();
    setSelectedIndex(null);
  };

  const nextImage = useCallback(() => {
    setSelectedIndex((prev) =>
      gallery.length === 0 ? prev : (prev + 1) % gallery.length
    );
  }, [gallery.length]);

  const prevImage = useCallback(() => {
    setSelectedIndex((prev) =>
      gallery.length === 0 ? prev : prev === 0 ? gallery.length - 1 : prev - 1
    );
  }, [gallery.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextImage, prevImage]);

  if (error) return null;

  const selectedItem =
    selectedIndex != null && gallery[selectedIndex]
      ? gallery[selectedIndex]
      : null;

  // Function to extract YouTube video ID and thumbnail
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/
    );
    return match ? match[1] : null;
  };

  const getYouTubeThumbnail = (url) => {
    const id = getYouTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
  };

  return (
    <div className="container mt-4 pt-2">
      {gallery.length > 0 && (
        <div className="mb-5">
          <h2 className="mb-4 text-center organizer-title">Gallery</h2>

          <div className="row g-3">
            {gallery.map((item, index) => (
              <div key={item.id} className="col-12 col-sm-6 col-lg-4">
                <div
                  className="h-100 border-1  gallery-card position-relative"
                  style={{ cursor: "pointer" }}
                  onClick={() => openModal(index)}
                >
                  {item.type === 2 ? (
                    <>
                      <img
                        src={getYouTubeThumbnail(item.link)}
                        alt={item.title}
                        className="gallery-image w-100 "
                      />
                    </>
                  ) : (
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="gallery-image w-100 "
                    />
                  )}
                  <div className="card-body text-center">
                    <h5 className="card-title gallery-text mt-3 mb-1">
                      {item.title}
                    </h5>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="text-center mt-5">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="btn btn-dark px-4 py-2"
              >
                {loading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <div
        className="modal fade"
        id="galleryModal"
        tabIndex="-1"
        aria-labelledby="galleryModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content bg-none text-black position-relative">
            <button
              type="button"
              className="btn-close position-absolute top-0 end-0 m-3"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={closeModal}
              style={{ zIndex: 1055 }}
            ></button>


            {selectedItem && (
              <>
                <div className="modal-body text-center pt-5 mt-4">
                  {/* If video type, show iframe */}
                  {selectedItem.type === 2 ? (
                    <iframe
                      width="100%"
                      height="500"
                      src={`https://www.youtube.com/embed/${getYouTubeId(
                        selectedItem.link
                      )}?autoplay=0&loop=0&controls=1`}
                      title={selectedItem.title}
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className=""
                    ></iframe>
                  ) : (
                    <div>
                      <img
                        src={selectedItem.image_url}
                        alt={selectedItem.title}
                        className="gallery-image-modal"
                      />
                    </div>
                  )}
                  <h5 className="mt-3">{selectedItem.title}</h5>
                </div>

                {/* Controls */}
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center justify-content-center position-absolute top-50 start-0 ms-2 translate-middle-y text-size-25"
                  onClick={prevImage}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center justify-content-center position-absolute top-50 end-0 me-2 translate-middle-y text-size-25"
                  onClick={nextImage}
                >
                  ›
                </button>


              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Gallery;
